import { useCallback, useEffect, useMemo, useState } from 'react';

type UseResendCooldownArgs = {
  seconds: number;
  tickMs?: number;
};

type UseResendCooldownResult = {
  secondsRemaining: number;
  onCooldown: boolean;
  startCooldown: () => void;
  clearCooldown: () => void;
};

export const useResendCooldown = ({
  seconds,
  tickMs = 1000,
}: UseResendCooldownArgs): UseResendCooldownResult => {
  const [availableAtMs, setAvailableAtMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (availableAtMs === null) return;
    if (availableAtMs <= Date.now()) {
      setAvailableAtMs(null);
      return;
    }
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, tickMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [availableAtMs, tickMs]);

  useEffect(() => {
    if (availableAtMs === null) return;
    if (availableAtMs <= nowMs) {
      setAvailableAtMs(null);
    }
  }, [availableAtMs, nowMs]);

  const startCooldown = useCallback(() => {
    const start = Date.now();
    setNowMs(start);
    setAvailableAtMs(start + seconds * 1000);
  }, [seconds]);

  const clearCooldown = useCallback(() => {
    setAvailableAtMs(null);
  }, []);

  const secondsRemaining = useMemo(
    () => (availableAtMs === null ? 0 : Math.max(0, Math.ceil((availableAtMs - nowMs) / 1000))),
    [availableAtMs, nowMs],
  );

  return {
    secondsRemaining,
    onCooldown: secondsRemaining > 0,
    startCooldown,
    clearCooldown,
  };
};
